import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { FileContractIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'

import InformationToolTip from '../../components/elements/information-tooltip'
import PeriodBadge from '../../components/elements/period-badge'
import StaffRemindersCell from '../../components/elements/staff-reminders-cell'
import MissionToStaffReportBadge from '../../components/pages/mission-trackings/mission-to-staff-report-badge'
import MissionTrackingRemindersCell from '../../components/pages/mission-trackings/mission-tracking-reminders-cell'
import {
  AllStaffDocumentTypeEnum,
  getAllStaffDocumentMapping
} from '../../lib/types/enums/common/document-status.enum'
import { missionStatusEnumMapping } from '../../lib/types/enums/mission/mission-status.enum'
import { MissionTracking } from '../../lib/types/models/mission-tracking/mission-tracking'
import { MissionTrackingProperties } from '../../lib/types/models/mission-tracking/properties.mission-tracking'

export const missionTrackingColumns: Column<MissionTracking>[] = [
  {
    accessorKey: 'staff.lastName',
    header: 'NOM',
    enableSorting: true,
    cell: ({ row }) => row.original.staff.lastName.toUpperCase()
  },
  {
    accessorKey: 'staff.firstName',
    header: 'Prénom',
    enableSorting: true,
    cell: ({ row }) => StringFormatter.capitalize(row.original.staff.firstName)
  },
  {
    accessorKey: 'startDate',
    header: 'Dates de contrat',
    enableSorting: true,
    cell: ({ row }) => (
      <PeriodBadge
        period={row.original.period ?? undefined}
        text={`${DateFormatter.DDMMYYYY(row.original.startDate)} - ${DateFormatter.DDMMYYYY(row.original.endDate)}`}
      />
    )
  },
  {
    accessorKey: 'missionsCount',
    header: '# missions',
    enableSorting: true
  },
  {
    accessorKey: 'affectedStaffs',
    header: 'Qualifications + Dates + Libellés des missions',
    cell: ({ row }) => (
      <div className={'flex flex-wrap py-2 gap-2'}>
        {row.original.affectedStaffs.map(({ mission }) => (
          <Badge
            key={mission.id}
            text={MissionTrackingProperties.getLabel(mission)}
            variant={missionStatusEnumMapping[mission.status].variant}
          />
        ))}
      </div>
    )
  },
  {
    accessorKey: 'missingDocumentsReminders',
    header: 'Relance doc',
    cell: ({ row }) => (
      <MissionTrackingRemindersCell
        title={'Historique des relances documents'}
        reminders={row.original.missingDocumentsReminders}
      />
    )
  },
  {
    accessorKey: 'contractReminders',
    header: 'Relance contrat',
    cell: ({ row }) => (
      <MissionTrackingRemindersCell
        title={'Historique des relances contrat'}
        reminders={row.original.contractReminders}
      />
    )
  },
  {
    accessorKey: 'reportReminders',
    header: 'Relance rapport',
    cell: ({ row }) => <StaffRemindersCell reminders={row.original.reportReminders} />
  },
  {
    accessorKey: 'staff.criminalRecordStatus',
    header: 'Documents',
    cell: ({ row }) => {
      const staff = row.original.staff
      const documents = getAllStaffDocumentMapping(staff, row.original.contract)
      const staffDocuments: AllStaffDocumentTypeEnum[] = [
        AllStaffDocumentTypeEnum.ID_FILE,
        AllStaffDocumentTypeEnum.CRIMINAL_RECORD
      ]

      return (
        <div className="flex gap-1">
          {staffDocuments.map((key) => (
            <InformationToolTip
              key={key}
              icon={
                <Badge
                  key={key}
                  variant={documents[key].variant}
                  icon={documents[key].icon}
                  iconPosition="start"
                />
              }
              size="small"
            >
              <Typography variant={'small-semibold'} color={tailwindColors.white.DEFAULT}>
                {documents[key].label}
              </Typography>
            </InformationToolTip>
          ))}
        </div>
      )
    }
  },
  {
    accessorKey: 'contractSentAt',
    header: 'Contrat envoyé',
    enableSorting: true,
    cell: ({ row }) =>
      !row.original.contract?.contractSentAt ? (
        '-'
      ) : (
        <Badge text={DateFormatter.DDMMYYYY(row.original.contract.contractSentAt)} />
      )
  },
  {
    accessorKey: 'contractSignedAt',
    header: 'Contrat signé',
    enableSorting: true,
    cell: ({ row }) => {
      const contract = row.original.contract
      const isSigned = !!contract?.contractSignedAt
      const isSent = !!contract?.contractSentAt
      const variant = isSigned ? 'success' : isSent ? 'danger' : 'grey'
      const color = isSigned
        ? tailwindColors.green.DEFAULT
        : isSent
          ? tailwindColors.red.DEFAULT
          : tailwindColors.grey.DEFAULT

      return (
        <div className="flex items-center gap-2">
          <InformationToolTip
            icon={
              <Badge
                variant={variant}
                icon={<FileContractIcon color={color} />}
                iconPosition="start"
              />
            }
            size="small"
          >
            <Typography variant={'small-semibold'} color={tailwindColors.white.DEFAULT}>
              {isSigned ? 'Contrat signé' : isSent ? 'Contrat non signé' : 'Contrat non envoyé'}
            </Typography>
          </InformationToolTip>
        </div>
      )
    }
  },

  {
    accessorKey: 'dpaeDownloadAt',
    header: 'DPAE',
    enableSorting: true,
    cell: ({ row }) =>
      !row.original.dpaeDownloadAt ? (
        '-'
      ) : (
        <Badge text={DateFormatter.DDMMYYYY(row.original.dpaeDownloadAt)} />
      )
  },
  {
    accessorKey: 'affectedStaffs',
    header: 'Rapports',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        {row.original.affectedStaffs.map((af) => (
          <MissionToStaffReportBadge
            key={af.id}
            missionToStaffId={af.id}
            reportStatus={af.reportStatus}
          />
        ))}
      </div>
    )
  }
]
